import { FC, useCallback, useMemo, PropsWithChildren } from 'react';
import { Box, Typography, SxProps } from '@mui/material';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Logo from '../logo/logo.component';
import { footerV2Styles } from './footer.styles';
import { useStyles } from '@sweatpals/react-ui-kit';
import { InterestsEnum, SUPPORT_EMAIL } from '@sweatpals/shared';
import useLink from '../../hooks/useLink';
import { TYPEFORM_URL, SOCIAL_MEDIA_LINKS, TYPEFORM_AMBASSADOR_URL, DASHBOARD_URL } from '../../constants';
import AppStoreButtons from '../app-store-buttons/app-store-buttons.component';
import SocialButtons from '../social-buttons/social-buttons.component';
import ExternalLink from '../links/External-link';

const LinkItem = ({
	sx,
	to,
	href,
	text,
	onClick,
	children,
}: PropsWithChildren<{ to?: string; href?: string; text?: string; sx?: SxProps; onClick?(): void }>) => (
	<Box
		sx={sx}
		component={(props) =>
			href || to?.startsWith('http') ? (
				<ExternalLink href={href || to} {...props} />
			) : to || href ? (
				<Link passHref href={to || href} {...props} legacyBehavior />
			) : (
				<div {...props} />
			)
		}
	>
		<Box sx={sx} onClick={onClick}>
			{text || children}
		</Box>
	</Box>
);

const FooterV2: FC = () => {
	const router = useRouter();
	const { makeSearchEventsLink, makeDeepAppLink } = useLink();

	const styles = useStyles(footerV2Styles);

	const onCategoryLinkClick = useCallback(
		({ locationPath, fitnessTypeIds }: { locationPath?: string; fitnessTypeIds?: string[] } = {}) =>
			() => {
				const link = makeSearchEventsLink({ place: locationPath, fitnessTypeIds });
				router.push(link);
			},
		[makeSearchEventsLink, router]
	);

	const eventCategories = useMemo(
		() => [
			{
				id: 'all events',
				name: 'all events',
			},
			{
				id: 'running',
				name: 'running',
				fitnessTypeIds: [InterestsEnum.Running],
			},
			{
				id: 'pickleball',
				name: 'pickleball',
				fitnessTypeIds: [InterestsEnum.Pickleball],
			},
			{
				id: 'volleyball',
				name: 'volleyball',
				fitnessTypeIds: [InterestsEnum.Volleyball],
			},
			{
				id: 'yoga',
				name: 'yoga',
				fitnessTypeIds: [InterestsEnum.Yoga, InterestsEnum.AcroYoga],
			},
			{
				id: 'walking',
				name: 'walking',
				fitnessTypeIds: [InterestsEnum.Walking],
			},
			{
				id: 'hiit',
				name: 'hiit',
				fitnessTypeIds: [InterestsEnum.HIIT],
			},
			{
				id: 'water',
				name: 'water',
				fitnessTypeIds: [
					InterestsEnum.Water,
					InterestsEnum.Swimming,
					InterestsEnum.Surfing,
					InterestsEnum.Icebath,
					InterestsEnum.PaddleBoarding,
					InterestsEnum.Rowing,
				],
			},
			{
				id: 'cycling',
				name: 'cycling',
				fitnessTypeIds: [InterestsEnum.Cycling],
			},
		],
		[]
	);

	const eventCategoriesLinks = useCallback(
		(locationPath: string) =>
			eventCategories.map((category) => (
				<LinkItem
					key={category.id}
					onClick={onCategoryLinkClick({
						locationPath,
						fitnessTypeIds: category.fitnessTypeIds,
					})}
					text={category.name}
					sx={styles.linkItem}
				/>
			)),
		[eventCategories, onCategoryLinkClick, styles.linkItem]
	);

	return (
		<Box sx={styles.root}>
			<Box sx={styles.logo}>
				<Logo />
			</Box>
			<Box sx={styles.links}>
				<Box sx={styles.linksColumn}>
					<Typography sx={styles.linksColumnTitle}>events in austin</Typography>
					<Box sx={styles.linksColumnContent}>{eventCategoriesLinks('austin')}</Box>
				</Box>
				<Box sx={styles.linksColumn}>
					<Typography sx={styles.linksColumnTitle}>events in miami area</Typography>
					<Box sx={styles.linksColumnContent}>{eventCategoriesLinks('miami')}</Box>
				</Box>
				<Box sx={styles.linksColumn}>
					<Typography sx={styles.linksColumnTitle}>business</Typography>
					<Box sx={styles.linksColumnContent}>
						<LinkItem href="/create/event" sx={styles.linkItem}>
							promote my events
						</LinkItem>
						<LinkItem href="/create/community" sx={styles.linkItem}>
							grow my community
						</LinkItem>
						<LinkItem href="/sweatpalsuniversity" sx={styles.linkItem}>
							sweatpals university
						</LinkItem>
						<LinkItem href={TYPEFORM_URL} sx={styles.linkItem}>
							brand partnerships
						</LinkItem>
						<LinkItem href={TYPEFORM_URL} sx={styles.linkItem}>
							corporate offerings
						</LinkItem>
						<LinkItem href={TYPEFORM_AMBASSADOR_URL} sx={styles.linkItem}>
							become an ambassador
						</LinkItem>
						<LinkItem href={TYPEFORM_URL} sx={styles.linkItem}>
							contact us
						</LinkItem>
					</Box>
				</Box>
				<Box sx={styles.linksColumn}>
					<Typography sx={styles.linksColumnTitle}>get sweatpals</Typography>
					<Box sx={styles.linksColumnContent}>
						<LinkItem href={makeDeepAppLink()} sx={styles.linkItem}>
							get the free app
						</LinkItem>
						<LinkItem to={DASHBOARD_URL} sx={styles.linkItem}>
							signup/login
						</LinkItem>
						<LinkItem href={`mailto:${SUPPORT_EMAIL}`} sx={styles.linkItem}>
							app support
						</LinkItem>
						<LinkItem to="/download" sx={styles.linkItem}>
							download page
						</LinkItem>
						{/* <LinkItem to="/" sx={styles.linkItem}>
							about us
						</LinkItem>
						<LinkItem to="/" sx={styles.linkItem}>
							blogs
						</LinkItem>
						<LinkItem to="/" sx={styles.linkItem}>
							site map
						</LinkItem> */}
					</Box>
				</Box>
			</Box>
			<Box sx={styles.copyright}>
				<Box sx={styles.copyrightContent}>
					<AppStoreButtons sx={styles.appStoreButtons} />
					<SocialButtons
						facebookUrl={SOCIAL_MEDIA_LINKS.facebook}
						instagramUrl={SOCIAL_MEDIA_LINKS.instagram}
						ticktockUrl={SOCIAL_MEDIA_LINKS.tiktok}
					/>
					<Typography sx={styles.copyrightText}>Made with ❤️ in Austin, TX.</Typography>
					<Typography sx={styles.copyrightCode}>©SWEATPALS {new Date().getFullYear()}</Typography>
				</Box>
			</Box>
		</Box>
	);
};

export default FooterV2;
